import { useState, useEffect } from "react";
import StudentTemplate from "../../component/StudentTemplate";
import Select from "react-select";
import Api from "../../component/Api";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

// ─── Design tokens ────────────────────────────────────────────────────────────
const COLOR = {
  primary: "#782849",
  primaryDark: "#5a1d37",
  primaryLight: "#a03060",
  bg: "#f8f4f6",
  cardBg: "#ffffff",
  border: "#f0e6ea",
  text: "#2d2d2d",
  textMuted: "#8a7080",
};

const MAX_INTERNSHIPS = 5;

const CARD_ACCENT_COLORS = [
  "#782849",
  "#2d6a9f",
  "#2e7d32",
  "#b45309",
  "#6d28d9",
  "#b91c1c",
  "#0e7490",
];

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  entered: {
    label: "Entered",
    bg: "#dbeafe",
    color: "#1d4ed8",
    dot: "#3b82f6",
  },
  principal_approved: {
    label: "Principal Approved",
    bg: "#dcfce7",
    color: "#166534",
    dot: "#22c55e",
  },
  principal_reverted: {
    label: "Principal Reverted",
    bg: "#fff7ed",
    color: "#9a3412",
    dot: "#f97316",
  },
  iso_forwarded: {
    label: "IS Forwarded",
    bg: "#f3e8ff",
    color: "#6b21a8",
    dot: "#a855f7",
  },
  iso_reverted: {
    label: "IS Reverted",
    bg: "#fef9c3",
    color: "#854d0e",
    dot: "#eab308",
  },
  ino_forwarded: {
    label: "INO Forwarded",
    bg: "#ccfbf1",
    color: "#134e4a",
    dot: "#14b8a6",
  },
  ino_reverted: {
    label: "INO Reverted",
    bg: "#fce7f3",
    color: "#9d174d",
    dot: "#ec4899",
  },
};
const getStatus = (s) =>
  STATUS_CONFIG[s] || {
    label: s,
    bg: "#f3f4f6",
    color: "#374151",
    dot: "#9ca3af",
  };
const isLocked = (s) =>
  ["principal_approved", "iso_forwarded", "ino_forwarded"].includes(s);

// ─── Reusable pill ────────────────────────────────────────────────────────────
const Pill = ({ children, bg, color, dot }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      background: bg,
      color,
      borderRadius: "20px",
      padding: "3px 11px",
      fontSize: "11.5px",
      fontWeight: 700,
      whiteSpace: "nowrap",
    }}
  >
    {dot && (
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: dot,
          flexShrink: 0,
        }}
      />
    )}
    {children}
  </span>
);

// ─── Info cell ────────────────────────────────────────────────────────────────
const InfoCell = ({ label, value, wide }) => (
  <div
    style={{
      background: "#faf7f8",
      borderRadius: "10px",
      padding: "12px 15px",
      border: `1px solid ${COLOR.border}`,
      gridColumn: wide ? "1/-1" : undefined,
    }}
  >
    <span
      style={{
        display: "block",
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.7px",
        textTransform: "uppercase",
        color: "#a07080",
        marginBottom: "5px",
      }}
    >
      {label}
    </span>
    <span
      style={{
        display: "block",
        fontSize: "13.5px",
        color: COLOR.text,
        fontWeight: 600,
        wordBreak: "break-word",
        whiteSpace: "pre-line",
      }}
    >
      {value || "—"}
    </span>
  </div>
);

// ─── Shared styles ────────────────────────────────────────────────────────────
const inp = {
  width: "100%",
  padding: "11px 14px",
  border: "1.5px solid #ddd",
  borderRadius: "10px",
  fontSize: "14px",
  background: "#f9f9f9",
  color: COLOR.text,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};
const sel = { ...inp, cursor: "pointer" };
const btn = {
  save: {
    background: `linear-gradient(135deg,${COLOR.primary},${COLOR.primaryLight})`,
    color: "#fff",
    border: "none",
    borderRadius: "9px",
    padding: "10px 26px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  cancel: {
    background: "#fff",
    color: "#555",
    border: "1.5px solid #ddd",
    borderRadius: "9px",
    padding: "10px 22px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  update: {
    background: `linear-gradient(135deg,${COLOR.primary},${COLOR.primaryLight})`,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "9px 20px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
  },
  remove: {
    background: "#fff",
    color: "#c62828",
    border: "1.5px solid #fca5a5",
    borderRadius: "8px",
    padding: "8px 18px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
  },
  add: {
    background: "#fff",
    color: COLOR.primary,
    border: "none",
    borderRadius: "10px",
    padding: "10px 20px",
    fontWeight: 700,
    fontSize: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    boxShadow: "0 2px 8px rgba(0,0,0,.12)",
  },
};

// ─── File Upload Box ──────────────────────────────────────────────────────────
const FileUploadBox = ({ uploadedFile, handleFileChange, existingFile }) => {
  const hasFile = !!uploadedFile;
  const hasExistingFile = !!existingFile;
  return (
    <div style={{ marginTop: "18px" }}>
      <label
        style={{
          display: "block",
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: ".6px",
          textTransform: "uppercase",
          color: "#6d3a52",
          marginBottom: "8px",
        }}
      >
        Supporting Document <span style={{ color: "#e53e3e" }}>*</span>
      </label>

      <label
        htmlFor="file-upload-notlisted"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          border: `2px dashed ${hasFile ? "#782849" : "#d8b4c4"}`,
          borderRadius: "12px",
          padding: "16px 20px",
          cursor: "pointer",
          background: hasFile ? "#fdf0f5" : "#faf7f8",
          transition: "all .2s",
        }}
      >
        <div
          style={{
            width: "44px",
            height: "44px",
            borderRadius: "10px",
            flexShrink: 0,
            background: hasFile ? "#782849" : "#f0e6ea",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
          }}
        >
          {hasFile ? "📄" : "📁"}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {hasFile ? (
            <>
              <p
                style={{
                  margin: 0,
                  fontWeight: 700,
                  fontSize: "13.5px",
                  color: "#782849",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {uploadedFile.name}
              </p>
              <p
                style={{
                  margin: "3px 0 0",
                  fontSize: "12px",
                  color: "#9a6070",
                }}
              >
                {(uploadedFile.size / 1024).toFixed(1)} KB — click to replace
              </p>
            </>
          ) : hasExistingFile ? (
            <>
              <p
                style={{
                  margin: 0,
                  fontWeight: 700,
                  fontSize: "13.5px",
                  color: "#782849",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {existingFile}
              </p>
              
            </>
          ) : (
            <>
              <p
                style={{
                  margin: 0,
                  fontWeight: 600,
                  fontSize: "13.5px",
                  color: "#6d3a52",
                }}
              >
                Click to upload document
              </p>
              <p
                style={{
                  margin: "3px 0 0",
                  fontSize: "12px",
                  color: "#b08090",
                }}
              >
                PDF — max 2 MB
              </p>
            </>
          )}
        </div>
        {hasFile && (
          <span
            style={{
              fontSize: "18px",
              color: "#22c55e",
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            ✔
          </span>
        )}
      </label>

      <input
        id="file-upload-notlisted"
        type="file"
        accept=".pdf"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </div>
  );
};

// ─── Form (defined OUTSIDE parent — prevents remount on every keystroke) ──────
const Form = (props) => {
  const {
    isUpdate,
    internshipSource,
    changeSource,
    selectedCourse,
    setSelectedCourse,
    selectedInternship,
    setSelectedInternship,
    internshipData,
    courseCode,
    externalInternship,
    handleExtChange,
    isIPO,
    getVendorData,
    loading,
    setIsFirstSubmit,
    setIsFirstSubmitExt,
    validateExternal,
    resetForm,
    setEditingId,
    setShowAddNew,
    setReload,
    reload,
    rSelect,
    ipoOptions,
    fmtOption,
    uploadedFile,
    handleFileChange,
  } = props;

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "16px",
        marginBottom: "16px",
        border: `2px solid ${COLOR.primaryLight}`,
        boxShadow: "0 6px 28px rgba(120,40,73,.13)",
      }}
    >
      {/* header */}
      <div
        style={{
          background: `linear-gradient(135deg, ${COLOR.primaryDark},${COLOR.primaryLight})`,
          padding: "16px 24px",
          borderTopLeftRadius: "14px",
          borderTopRightRadius: "14px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <span style={{ fontSize: "20px" }}>{isUpdate ? "✏️" : "➕"}</span>
        <h3
          style={{
            margin: 0,
            color: "#fff",
            fontSize: "16px",
            fontWeight: 700,
          }}
        >
          {isUpdate ? "Update Internship Application" : "Add New Internship"}
        </h3>
      </div>

      <div style={{ padding: "24px", borderRadius: "16px" }}>
        {/* Source */}
        <div style={{ marginBottom: "18px" }}>
          <label
            style={{
              display: "block",
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: ".6px",
              textTransform: "uppercase",
              color: "#6d3a52",
              marginBottom: "8px",
            }}
          >
            Internship Providing Organisation (IPO){" "}
            <span style={{ color: "#e53e3e" }}>*</span>
          </label>
          <Select
            styles={rSelect}
            options={ipoOptions}
            value={ipoOptions.find((o) => o.value === internshipSource) || null}
            onChange={(opt) => changeSource(opt?.value || "")}
            placeholder="--- Select organisation type ---"
            isClearable
          />
        </div>

        {/* COLLEGE */}
        {internshipSource === "COLLEGE" && (
          <>
            <hr
              style={{
                border: "none",
                borderTop: `1px solid ${COLOR.border}`,
                margin: "0 0 18px",
              }}
            />
            <div style={{ marginBottom: "18px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: ".6px",
                  textTransform: "uppercase",
                  color: "#6d3a52",
                  marginBottom: "8px",
                }}
              >
                Internship Topic <span style={{ color: "#e53e3e" }}>*</span>
              </label>
              <Select
                styles={rSelect}
                options={courseCode ? internshipData.map(fmtOption) : []}
                value={
                  selectedCourse
                    ? internshipData
                        .map(fmtOption)
                        .find((o) => o.value === Number(selectedCourse))
                    : null
                }
                onChange={(opt) => {
                  const v = opt?.value || "";
                  setSelectedCourse(v);
                  setSelectedInternship(
                    internshipData.find((i) => i.id === Number(v)) || null,
                  );
                }}
                placeholder="-- Search and select topic --"
                isClearable
                formatOptionLabel={(o) => (
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{o.topic}</span>
                    <span style={{ color: COLOR.primary, fontSize: "12px" }}>
                      | {o.provider}
                    </span>
                  </div>
                )}
              />
            </div>
            {selectedInternship && (
              <div
                style={{
                  background: "#fdf6f9",
                  borderRadius: "12px",
                  padding: "16px",
                  border: `1px solid ${COLOR.border}`,
                }}
              >
                <p
                  style={{
                    margin: "0 0 12px",
                    fontSize: "11px",
                    fontWeight: 800,
                    color: COLOR.primary,
                    textTransform: "uppercase",
                    letterSpacing: ".7px",
                  }}
                >
                  Preview
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
                    gap: "12px",
                  }}
                >
                  <InfoCell
                    label="Offered By"
                    value={selectedInternship.offered_by_name}
                  />
                  {internshipSource === "COLLEGE" ? (
                    <InfoCell
                      label="Mode of Study"
                      value={selectedInternship.mode_of_study}
                    />
                  ) : (
                    <div
                      style={{
                        background: "#faf7f8",
                        borderRadius: "10px",
                        padding: "12px 15px",
                        border: `1px solid ${COLOR.border}`,
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          fontSize: "10.5px",
                          fontWeight: 700,
                          letterSpacing: ".7px",
                          textTransform: "uppercase",
                          color: "#a07080",
                          marginBottom: "5px",
                        }}
                      >
                        Mode of Study
                      </span>
                      <select
                        value={selectedInternship.mode_of_study}
                        style={{ ...sel, padding: "5px 8px", fontSize: "13px" }}
                        onChange={(e) =>
                          setSelectedInternship({
                            ...selectedInternship,
                            mode_of_study: e.target.value,
                          })
                        }
                      >
                        <option value="Online">Online</option>
                        <option value="Offline">Offline</option>
                        <option value="Hybrid">Hybrid</option>
                      </select>
                    </div>
                  )}
                  <InfoCell
                    label="Vendor Mobile"
                    value={selectedInternship.vendor_mobileno}
                  />
                  <InfoCell
                    label="Vendor Email"
                    value={selectedInternship.vendor_email}
                  />
                  <InfoCell
                    label="Vendor Address"
                    value={selectedInternship.vendor_address}
                    wide
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* IPO / NOT_LISTED */}
        {(internshipSource === "IPO" || internshipSource === "NOT_LISTED") && (
          <>
            <hr
              style={{
                border: "none",
                borderTop: `1px solid ${COLOR.border}`,
                margin: "0 0 18px",
              }}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              {/* Title */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: ".6px",
                    textTransform: "uppercase",
                    color: "#6d3a52",
                    marginBottom: "8px",
                  }}
                >
                  Internship Title <span style={{ color: "#e53e3e" }}>*</span>
                </label>
                <input
                  style={inp}
                  type="text"
                  name="title"
                  value={externalInternship.title}
                  onChange={handleExtChange}
                  placeholder="Enter internship title"
                />
              </div>

              {/* Mode */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: ".6px",
                    textTransform: "uppercase",
                    color: "#6d3a52",
                    marginBottom: "8px",
                  }}
                >
                  Mode of Internship <span style={{ color: "#e53e3e" }}>*</span>
                </label>
                <select
                  style={sel}
                  name="mode"
                  value={externalInternship.mode}
                  onChange={handleExtChange}
                >
                  <option value="">-- Select Mode --</option>
                  {["Online", "Offline", "Hybrid"].map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {/* Provider */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: ".6px",
                    textTransform: "uppercase",
                    color: "#6d3a52",
                    marginBottom: "8px",
                  }}
                >
                  Internship Provider{" "}
                  <span style={{ color: "#e53e3e" }}>*</span>
                </label>
                {isIPO ? (
                  <select
                    style={sel}
                    name="provider"
                    value={externalInternship.provider}
                    onChange={handleExtChange}
                  >
                    <option value="">-- Select Provider --</option>
                    {getVendorData?.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.vendor_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    style={inp}
                    type="text"
                    name="provider_name"
                    value={
                      externalInternship.provider_name ||
                      externalInternship.provider
                    }
                    onChange={handleExtChange}
                    placeholder="Enter provider name"
                  />
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: ".6px",
                  textTransform: "uppercase",
                  color: "#6d3a52",
                  marginBottom: "8px",
                }}
              >
                Description
              </label>
              <textarea
                style={{ ...inp, resize: "vertical" }}
                name="description"
                rows={4}
                value={externalInternship.description}
                onChange={handleExtChange}
                placeholder="Brief description of the internship..."
              />
            </div>

            {/* File Upload — only for NOT_LISTED */}
            {internshipSource === "NOT_LISTED" && (
              <FileUploadBox
                uploadedFile={uploadedFile}
                handleFileChange={handleFileChange}
                existingFile={externalInternship.consent_file}
              />
            )}
          </>
        )}
      </div>

      {/* footer */}
      <div
        style={{
          padding: "16px 24px",
          borderRadius: "16px",
          borderTop: `1px solid ${COLOR.border}`,
          background: "#fdf6f9",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
        }}
      >
        <button
          style={btn.cancel}
          onClick={() => {
            resetForm();
            setEditingId(null);
            setShowAddNew(false);
            setReload(!reload);
          }}
        >
          Cancel
        </button>
        {internshipSource === "COLLEGE" ? (
          <button
            style={{ ...btn.save, opacity: loading ? 0.6 : 1 }}
            onClick={() => setIsFirstSubmit(true)}
            disabled={loading}
          >
            {loading ? "Saving…" : isUpdate ? "Update" : "Save"}
          </button>
        ) : (
          <button
            style={{ ...btn.save, opacity: loading ? 0.6 : 1 }}
            onClick={() => {
              if (validateExternal()) setIsFirstSubmitExt(true);
            }}
            disabled={loading}
          >
            {loading ? "Saving…" : isUpdate ? "Update" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Card (defined OUTSIDE parent — prevents remount on state change) ─────────
const Card = ({
  saved,
  idx,
  editingId,
  setEditingId,
  isEventActive,
  getActiveEvents,
  courseCode,
  courseName,
  fmtDate,
  handleDelete,
  populateForm,
  setShowAddNew,
  internshipSource,
  changeSource,
  selectedCourse,
  setSelectedCourse,
  selectedInternship,
  setSelectedInternship,
  internshipData,
  externalInternship,
  handleExtChange,
  isIPO,
  getVendorData,
  loading,
  setIsFirstSubmit,
  setIsFirstSubmitExt,
  validateExternal,
  resetForm,
  setReload,
  reload,
  rSelect,
  ipoOptions,
  fmtOption,
  uploadedFile,
  handleFileChange,
}) => {
  if (editingId === saved.id) {
    return (
      <Form
        isUpdate={true}
        internshipSource={internshipSource}
        changeSource={changeSource}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        selectedInternship={selectedInternship}
        setSelectedInternship={setSelectedInternship}
        internshipData={internshipData}
        courseCode={courseCode}
        externalInternship={externalInternship}
        handleExtChange={handleExtChange}
        isIPO={isIPO}
        getVendorData={getVendorData}
        loading={loading}
        setIsFirstSubmit={setIsFirstSubmit}
        setIsFirstSubmitExt={setIsFirstSubmitExt}
        validateExternal={validateExternal}
        resetForm={resetForm}
        setEditingId={setEditingId}
        setShowAddNew={setShowAddNew}
        setReload={setReload}
        reload={reload}
        rSelect={rSelect}
        ipoOptions={ipoOptions}
        fmtOption={fmtOption}
        uploadedFile={uploadedFile}
        handleFileChange={handleFileChange}
      />
    );
  }

  const locked = isLocked(saved.entry_status);
  const canDelete = saved.entry_status === "entered";
  const st = getStatus(saved.entry_status);
  const accent = CARD_ACCENT_COLORS[idx % CARD_ACCENT_COLORS.length];
  const topicName =
    saved.type === "internal"
      ? saved.internship_topic
      : saved.external_topic_name || "External Internship";
  const provider =
    saved.type === "internal" ? saved.vendor_name : saved.external_ipo_name;
  const typeIcon = saved.type === "internal" ? "🏢" : "🌐";

  return (
    <div
      style={{
        background: COLOR.cardBg,
        borderRadius: "16px",
        boxShadow: "0 2px 14px rgba(0,0,0,.07)",
        marginBottom: "16px",
        border: `1px solid ${COLOR.border}`,
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex" }}>
        <div style={{ width: "5px", background: accent, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          {/* header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "16px 20px",
              borderBottom: `1px solid ${COLOR.border}`,
              background: "linear-gradient(90deg,#fdf6f9 0%,#fff 100%)",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                minWidth: "38px",
                height: "38px",
                borderRadius: "10px",
                background: accent,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px",
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {idx + 1}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4
                style={{
                  margin: 0,
                  fontSize: "15px",
                  fontWeight: 700,
                  color: COLOR.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={topicName}
              >
                {topicName}
              </h4>
              {provider && (
                <p
                  style={{
                    margin: "3px 0 0",
                    fontSize: "12.5px",
                    color: COLOR.textMuted,
                  }}
                >
                  {typeIcon} {provider}
                </p>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Pill
                bg={saved.type === "internal" ? "#dcfce7" : "#e0e7ff"}
                color={saved.type === "internal" ? "#166534" : "#3730a3"}
              >
                {typeIcon} {saved.type === "internal" ? "Internal" : "External"}
              </Pill>
              <Pill bg={st.bg} color={st.color} dot={st.dot}>
                {st.label}
              </Pill>
              {courseCode && (
                <Pill bg="#fff7ed" color="#92400e">
                  📋 {courseCode}
                </Pill>
              )}
            </div>
          </div>

          {/* body */}
          <div style={{ padding: "18px 20px" }}>
            {saved.type === "internal" ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))",
                  gap: "12px",
                }}
              >
                <InfoCell label="Offered By" value={saved.vendor_name} />
                <InfoCell label="Mode of Study" value={saved.mode_of_study} />
                <InfoCell label="Vendor Mobile" value={saved.vendor_mobileno} />
                <InfoCell label="Vendor Email" value={saved.vendor_email} />
                <InfoCell
                  label="Vendor Address"
                  value={saved.vendor_address}
                  wide
                />
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))",
                    gap: "12px",
                  }}
                >
                  <InfoCell
                    label="Internship Title"
                    value={saved.external_topic_name}
                  />
                  <InfoCell label="Provider" value={saved.external_ipo_name} />
                  <InfoCell label="Mode" value={saved.mode_of_study} />
                  {courseCode && (
                    <InfoCell
                      label="Course"
                      value={`${courseCode} – ${courseName}`}
                    />
                  )}
                </div>
                {saved.external_ipo_description && (
                  <div style={{ marginTop: "12px" }}>
                    <InfoCell
                      label="Description"
                      value={saved.external_ipo_description}
                      wide
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "13px 20px",
              borderTop: `1px solid ${COLOR.border}`,
              background: "#fdf6f9",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <span
              style={{
                fontSize: "12px",
                color: locked
                  ? "#c62828"
                  : isEventActive
                    ? "#7a5230"
                    : "#b45309",
                fontWeight: 600,
              }}
            >
              {locked
                ? "🔒 Forwarded/Approved — cannot be edited"
                : isEventActive
                  ? `⏳ Update until ${fmtDate(getActiveEvents?.end_date)}`
                  : "⛔ Application window is closed"}
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              {canDelete && (
                <button
                  style={btn.remove}
                  onClick={() => handleDelete(saved.id)}
                >
                  🗑 Remove
                </button>
              )}
              {isEventActive && !locked && (
                <button
                  style={btn.update}
                  onClick={() => {
                    setEditingId(saved.id);
                    setShowAddNew(false);
                    populateForm(saved);
                  }}
                >
                  ✏️ Update
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
const StudentInternship = () => {
  const [internshipData, setInternshipData] = useState([]);
  const [studentDisciplineName, setStudentDisciplineName] = useState("");
  const [internshipStudentData, setInternshipStudentData] = useState({});
  const [getActiveEvents, setActiveEvents] = useState(null);
  const [eventMessage, setEventMessage] = useState("");
  const [isEventActive, setIsEventActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [courseCode, setCourseCode] = useState(null);
  const [courseName, setCourseName] = useState(null);
  const [activeReadOnlyMode, setActiveReadOnlyMode] = useState(false);
  const [activeReadOnlyMessage, setActiveReadOnlyMessage] = useState("");
  const [getVendorData, setVendorData] = useState([]);

  const [savedInternships, setSavedInternships] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAddNew, setShowAddNew] = useState(false);

  const [internshipSource, setInternshipSource] = useState("");
  const [isIPO, setIsIPO] = useState(false);
  const [isNotListed, setIsNotListed] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedInternship, setSelectedInternship] = useState(null);
  const [externalInternship, setExternalInternship] = useState({
    title: "",
    provider: "",
    provider_name: "",
    duration: "",
    mode: "",
    description: "",
  });
  const [isFirstSubmit, setIsFirstSubmit] = useState(false);
  const [isFirstSubmitExt, setIsFirstSubmitExt] = useState(false);

  // ─── File upload state ─────────────────────────────────────────────────────
  const [uploadedFile, setUploadedFile] = useState(null);

  const handleFileChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  if (file.type !== "application/pdf") {
    toast.error("Only PDF files are allowed.");
    e.target.value = "";
    return;
  }

  if (file.size > 2 * 1024 * 1024) {
    toast.error("File size must be less than 2 MB.");
    e.target.value = "";
    return;
  }

  setUploadedFile(file);
};

  const { http } = Api();
  const navigate = useNavigate();
  const student_scheme_id = localStorage.getItem("scheme_id");

  const fmtDate = (s) => {
    if (!s) return "";
    const [dp, tp] = s.split(" ");
    const [yr, mo, dy] = dp.split("-");
    let [h, m, sc] = tp.split(":");
    let p = "AM";
    h = Number(h);
    if (h >= 12) {
      p = "PM";
      if (h > 12) h -= 12;
    }
    if (h === 0) h = 12;
    return `${dy}-${mo}-${yr} ${String(h).padStart(2, "0")}:${m}:${sc} ${p}`;
  };

  const resetForm = () => {
    setInternshipSource("");
    setIsIPO(false);
    setIsNotListed(false);
    setSelectedCourse("");
    setSelectedInternship(null);
    setExternalInternship({
      title: "",
      provider: "",
      provider_name: "",
      duration: "",
      mode: "",
      description: "",
    });
    setIsFirstSubmit(false);
    setIsFirstSubmitExt(false);
    setUploadedFile(null);
  };

  const populateForm = (saved) => {
    setInternshipSource(saved.internship_type);
    if (saved.type === "internal") {
      setIsNotListed(false);
      setIsIPO(false);
      setSelectedCourse(saved.topic_id);
      setSelectedInternship({
        id: saved.topic_id,
        internship_topic: saved.internship_topic,
        mode_of_study: saved.mode_of_study,
        offered_by_name: saved.vendor_name,
        vendor_mobileno: saved.vendor_mobileno,
        vendor_email: saved.vendor_email,
        vendor_address: saved.vendor_address,
      });
    } else {
      const ipo = saved.internship_type === "IPO";
      setIsIPO(ipo);
      setIsNotListed(true);
      setExternalInternship({
        title: saved.external_topic_name || "",
        provider: saved.external_ipo_id || "",
        provider_name: saved.external_ipo_name || "",
        duration: saved.external_ipo_duration || "",
        mode: saved.mode_of_study || "",
        description: saved.external_ipo_description || "",
        consent_file: saved.consent_file || null,
      });
    }
  };

  // ─── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!Object.keys(internshipStudentData).length) return;
    if (!internshipStudentData?.course_code) {
      const msg =
        "You have not registered for the internship course in the course selection.";
      setActiveReadOnlyMode(true);
      setActiveReadOnlyMessage(msg);
      toast.warning(msg, { autoClose: 5000 });
    } else if (!internshipStudentData?.iso_mapped_accountid) {
      const msg =
        "Internship Supervisor (IS) is not mapped to your profile. Please contact your college.";
      setActiveReadOnlyMode(true);
      setActiveReadOnlyMessage(msg);
      toast.warning(msg, { autoClose: 5000 });
    } else {
      setCourseCode(internshipStudentData.course_code);
      setCourseName(internshipStudentData.course_name);
      setActiveReadOnlyMode(false);
      setActiveReadOnlyMessage("");
    }
  }, [internshipStudentData]);

  useEffect(() => {
    http
      .get("/College/listTopicstudent")
      .then((r) => {
        if (Array.isArray(r.data?.data)) {
          setInternshipData(r.data.data);
          setStudentDisciplineName(r.data.student.discipline_name);
          setInternshipStudentData(r.data.student);
        }
      })
      .catch((err) => {
        const e = err.response?.data;
        if (e?.http_status === 401 && e?.status_code === "ERR9") {
          toast.error("Invalid Token");
          setTimeout(() => navigate("/"), 5000);
        } else toast.error("Something went wrong");
      });
  }, [reload]);

  useEffect(() => {
    if (!getActiveEvents) return;
    http
      .get("/College/getsavedInternshipTopic")
      .then((r) => {
        if (r.data?.status_code !== "MSG3") return;
        const saved = r.data.data;
        setSavedInternships(
          Array.isArray(saved) ? saved : saved ? [saved] : [],
        );
      })
      .catch((err) => {
        const st = err.response?.status;
        const code = err.response?.data?.status_code;
        const msg = err.response?.data?.message || "";
        if (
          msg.includes("user_id") ||
          (st === 500 && msg.includes("user_id"))
        ) {
          toast.error("Session expired.");
          setTimeout(() => navigate("/"), 3000);
        } else if (st === 403 && code === "ERR37") {
          toast.error("Invalid Token");
          setTimeout(() => navigate("/"), 3000);
        } else toast.error("Failed to load saved internship details");
      });
  }, [getActiveEvents, reload]);

  useEffect(() => {
    if (
      !internshipStudentData.admissionyear ||
      !internshipStudentData.currentsemester
    )
      return;
    http
      .get("getActiveEvents", { params: { event_id: 33 } })
      .then((r) => {
        const events = Array.isArray(r.data) ? r.data : [r.data];
        const match = events.find(
          (e) =>
            Number(e.admission_year) ===
              Number(internshipStudentData.admissionyear) &&
            Number(e.semester_id) ===
              Number(internshipStudentData.currentsemester) &&
            e.status === "active" &&
            e.scheme_id === Number(student_scheme_id),
        );
        if (match) {
          setActiveEvents(match);
          setIsEventActive(true);
          setEventMessage(
            `Application window open — submit on or before ${fmtDate(match.end_date)}.`,
          );
        } else {
          setActiveEvents(null);
          setIsEventActive(false);
          setEventMessage(
            "The internship application window is currently closed.",
          );
        }
      })
      .catch(() => {
        setActiveEvents(null);
        setIsEventActive(false);
        toast.error("Something went wrong");
      });
  }, [
    internshipStudentData.admissionyear,
    internshipStudentData.currentsemester,
  ]);

  useEffect(() => {
    http
      .get("/College/UniversityAddedVendor")
      .then((r) => setVendorData(r.data.vendors))
      .catch(() => setVendorData([]));
  }, []);

  // ─── Validation ────────────────────────────────────────────────────────────
  const validateExternal = () => {
    if (!externalInternship.title?.trim()) {
      toast.error("Please enter Internship Title");
      return false;
    }
    if (isIPO && !externalInternship.provider) {
      toast.error("Please select Internship Provider");
      return false;
    }
    if (
      !isIPO &&
      !externalInternship.provider_name?.trim() &&
      !externalInternship.provider?.trim()
    ) {
      toast.error("Please enter Internship Provider");
      return false;
    }
    if (!externalInternship.mode) {
      toast.error("Please select Mode of Internship");
      return false;
    }
    if (internshipSource === "NOT_LISTED" && !uploadedFile && !externalInternship.consent_file) {
      toast.error("Please upload a supporting document");
      return false;
    }
    return true;
  };

  // ─── Submit internal ───────────────────────────────────────────────────────
  const submitInternal = () => {
    if (!selectedInternship) return;
    if (!editingId && savedInternships.length >= MAX_INTERNSHIPS) {
      toast.error(
        `Maximum ${MAX_INTERNSHIPS} internship applications allowed.`,
      );
      return;
    }
    setLoading(true);
    const payload = {
      topic_id: selectedInternship.id,
      mode_of_study: selectedInternship.mode_of_study,
      type: "internal",
      internship_type: internshipSource,
    };
    const request = editingId
      ? http.post(`/College/updateStudentInternshipTopic/${editingId}`, payload)
      : http.post("/College/saveInternshipTopic", payload);

    request
      .then((r) => {
        if (r.data?.status_code === "MSG3" || r.status === 200) {
          toast.success(editingId ? "Updated!" : "Saved!");
          resetForm();
          setEditingId(null);
          setShowAddNew(false);
          setReload((p) => !p);
        }
      })
      .catch((error) => {
        const data = error.response?.data;
        toast.error(
          data?.status === false ? data.message : "Something went wrong.",
        );
      })
      .finally(() => setLoading(false));
  };

  // ─── Submit external ───────────────────────────────────────────────────────
  const submitExternal = () => {
    if (!editingId && savedInternships.length >= MAX_INTERNSHIPS) {
      toast.error(
        `Maximum ${MAX_INTERNSHIPS} internship applications allowed.`,
      );
      return;
    }
    setLoading(true);

    const payload = {
      topic_id: null,
      type: "external",
      internship_type: internshipSource,
      external_topic_name: externalInternship.title,
      external_ipo_id: isIPO ? externalInternship.provider || null : null,
      external_ipo_name: isIPO
        ? getVendorData.find((v) => v.id == externalInternship.provider)
            ?.vendor_name || ""
        : externalInternship.provider_name || externalInternship.provider,
      external_ipo_duration: externalInternship.duration,
      mode_of_study: externalInternship.mode,
      external_ipo_description: externalInternship.description,
    };

    let requestData;
    let requestConfig = {};

    if (internshipSource === "NOT_LISTED" && uploadedFile) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        fd.append(k, v === null || v === undefined ? "" : v);
      });
      fd.append("document", uploadedFile);
      requestData = fd;
      requestConfig = {
        headers: { "Content-Type": "multipart/form-data" }, // ← force correct content type
      };
    } else {
      requestData = payload;
    }

    const request = editingId
      ? http.post(
          `/College/updateStudentInternshipTopic/${editingId}`,
          requestData,
          requestConfig,
        )
      : http.post("/College/saveInternshipTopic", requestData, requestConfig);

    request
      .then((r) => {
        if (r.status === 200) {
          toast.success(editingId ? "Updated!" : "Saved!");
          resetForm();
          setEditingId(null);
          setShowAddNew(false);
          setReload((p) => !p);
        }
      })
      .catch((error) => {
        const data = error.response?.data;
        toast.error(
          data?.status === false ? data.message : "Something went wrong.",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleDelete = (id) => {
    if (!window.confirm("Remove this internship application?")) return;
    http
      .post(`/College/deleteInternshipTopic/${id}`)
      .then(() => {
        toast.success("Topic Deleted successfully.");
        setReload((p) => !p);
      })
      .catch(() => toast.error("Failed to delete topic."));
  };

  const handleExtChange = (e) => {
    const { name, value } = e.target;
    setExternalInternship((prev) => {
      if (name === "provider") {
        if (isIPO) {
          const v = getVendorData.find((x) => x.id == value);
          return {
            ...prev,
            provider: value,
            provider_name: v?.vendor_name || "",
          };
        }
        return { ...prev, provider: value, provider_name: value };
      }
      if (name === "provider_name") {
        return { ...prev, provider_name: value, provider: value };
      }
      return { ...prev, [name]: value };
    });
  };

  const changeSource = (val) => {
    setInternshipSource(val || "");
    setSelectedCourse("");
    setSelectedInternship(null);
    setExternalInternship({
      title: "",
      provider: "",
      provider_name: "",
      duration: "",
      mode: "",
      description: "",
    });
    setUploadedFile(null);
    if (val === "COLLEGE") {
      setIsIPO(false);
      setIsNotListed(false);
    } else if (val === "IPO") {
      setIsIPO(true);
      setIsNotListed(true);
    } else if (val === "NOT_LISTED") {
      setIsIPO(false);
      setIsNotListed(true);
    } else {
      setIsIPO(false);
      setIsNotListed(false);
    }
  };

  // ─── react-select styles ──────────────────────────────────────────────────
  const rSelect = {
    control: (p, s) => ({
      ...p,
      borderRadius: "10px",
      border: "1.5px solid",
      borderColor: s.isFocused ? COLOR.primary : "#ddd",
      backgroundColor: "#f9f9f9",
      minHeight: "44px",
      boxShadow: s.isFocused ? `0 0 0 2px rgba(120,40,73,.15)` : "none",
      "&:hover": { borderColor: COLOR.primary },
    }),
    valueContainer: (p) => ({ ...p, padding: "6px 12px" }),
    placeholder: (p) => ({ ...p, color: "#bbb" }),
    singleValue: (p) => ({ ...p, color: COLOR.text, fontWeight: 600 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (p, s) => ({
      ...p,
      color: COLOR.primary,
      transform: s.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
    }),
    menu: (p) => ({
      ...p,
      borderRadius: "10px",
      overflow: "hidden",
      marginTop: "6px",
      zIndex: 999,
    }),
    option: (p, s) => ({
      ...p,
      padding: "10px 14px",
      backgroundColor: s.isSelected
        ? "#e8d6dd"
        : s.isFocused
          ? "#f2e7ec"
          : "#fff",
      color: "#222",
      cursor: "pointer",
    }),
  };

  const ipoOptions = [
    { value: "COLLEGE", label: "🏫  College Enlisted" },
    { value: "IPO", label: "🏛️  University Enlisted" },
    { value: "NOT_LISTED", label: "🌐  Others" },
  ];
  const fmtOption = (item) => ({
    value: item.id,
    label: `${item.internship_topic} ${item.offered_by_name}`,
    topic: item.internship_topic,
    provider: item.offered_by_name,
  });

  // ─── Shared props ──────────────────────────────────────────────────────────
  const cardProps = {
    editingId,
    setEditingId,
    isEventActive,
    getActiveEvents,
    courseCode,
    courseName,
    fmtDate,
    handleDelete,
    populateForm,
    setShowAddNew,
    internshipSource,
    changeSource,
    selectedCourse,
    setSelectedCourse,
    selectedInternship,
    setSelectedInternship,
    internshipData,
    externalInternship,
    handleExtChange,
    isIPO,
    getVendorData,
    loading,
    setIsFirstSubmit,
    setIsFirstSubmitExt,
    validateExternal,
    resetForm,
    setReload,
    reload,
    rSelect,
    ipoOptions,
    fmtOption,
    uploadedFile,
    handleFileChange,
  };

  const formProps = {
    internshipSource,
    changeSource,
    selectedCourse,
    setSelectedCourse,
    selectedInternship,
    setSelectedInternship,
    internshipData,
    courseCode,
    externalInternship,
    handleExtChange,
    isIPO,
    getVendorData,
    loading,
    setIsFirstSubmit,
    setIsFirstSubmitExt,
    validateExternal,
    resetForm,
    setEditingId,
    setShowAddNew,
    setReload,
    reload,
    rSelect,
    ipoOptions,
    fmtOption,
    uploadedFile,
    handleFileChange,
  };

  // ─── Confirm modal ─────────────────────────────────────────────────────────
  const ConfirmModal = ({ isExt }) => {
    const onCancel = isExt
      ? () => setIsFirstSubmitExt(false)
      : () => setIsFirstSubmit(false);
    const onConfirm = isExt ? submitExternal : submitInternal;
    const subject = isExt
      ? externalInternship.title
      : selectedInternship?.internship_topic;
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,.55)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            overflow: "hidden",
            width: "100%",
            maxWidth: "440px",
            margin: "16px",
          }}
        >
          <div
            style={{
              background: `linear-gradient(135deg,${COLOR.primaryDark},${COLOR.primaryLight})`,
              padding: "18px 24px",
            }}
          >
            <h4 style={{ margin: 0, color: "#fff", fontSize: "16px" }}>
              Confirm {editingId ? "Update" : "Submission"}
            </h4>
          </div>
          <div style={{ padding: "24px" }}>
            <p style={{ color: "#444", lineHeight: 1.6, margin: "0 0 20px" }}>
              You are about to {editingId ? "update" : "submit"} the internship
              application for <strong>{subject}</strong>.
              {courseCode && (
                <>
                  <br />
                  Course:{" "}
                  <strong>
                    {courseCode} – {courseName}
                  </strong>
                </>
              )}
              <br />
              Please confirm to proceed.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <button style={btn.cancel} onClick={onCancel}>
                Cancel
              </button>
              <button
                style={{ ...btn.save, opacity: loading ? 0.6 : 1 }}
                onClick={onConfirm}
                disabled={loading}
              >
                {loading ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <StudentTemplate>
      <ToastContainer />
      <div style={{ fontFamily: "'Segoe UI',sans-serif", padding: "4px" }}>
        {/* Header */}
        <div
          style={{
            background: `linear-gradient(135deg,${COLOR.primaryDark} 0%,${COLOR.primaryLight} 100%)`,
            borderRadius: "16px",
            padding: "20px 26px",
            marginBottom: "20px",
            boxShadow: `0 4px 20px rgba(120,40,73,.28)`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "14px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                background: "rgba(255,255,255,.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
              }}
            >
              🎓
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  color: "#fff",
                  fontSize: "18px",
                  fontWeight: 800,
                }}
              >
                Internship Applications
              </h2>
              <p
                style={{
                  margin: "3px 0 0",
                  color: "rgba(255,255,255,.72)",
                  fontSize: "13px",
                }}
              >
                {studentDisciplineName || "Student Portal"}
              </p>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,.18)",
                border: "1.5px solid rgba(255,255,255,.35)",
                borderRadius: "12px",
                padding: "8px 16px",
                textAlign: "center",
              }}
            >
              <span
                style={{
                  display: "block",
                  color: "#fff",
                  fontSize: "24px",
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                {savedInternships.length}
              </span>
              <span
                style={{
                  color: "rgba(255,255,255,.75)",
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: ".6px",
                }}
              >
                {savedInternships.length === 1 ? "Application" : "Applications"}
              </span>
            </div>
            {isEventActive && savedInternships.length < MAX_INTERNSHIPS && (
              <button
                style={btn.add}
                onClick={() => {
                  resetForm();
                  setShowAddNew(true);
                }}
              >
                ＋ Add Internship
              </button>
            )}
            {isEventActive && savedInternships.length >= MAX_INTERNSHIPS && (
              <div
                style={{
                  background: "rgba(255,255,255,.18)",
                  border: "1.5px solid rgba(255,255,255,.35)",
                  borderRadius: "10px",
                  padding: "8px 14px",
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "rgba(255,255,255,.85)",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                🔒 Limit reached ({MAX_INTERNSHIPS}/{MAX_INTERNSHIPS})
              </div>
            )}
          </div>
        </div>

        {/* Notices */}
        {eventMessage && (
          <div
            style={{
              borderRadius: "10px",
              padding: "12px 18px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "13.5px",
              fontWeight: 500,
              background: isEventActive ? "#ecfdf5" : "#fff8e1",
              color: isEventActive ? "#065f46" : "#92400e",
              border: `1px solid ${isEventActive ? "#6ee7b7" : "#fde68a"}`,
            }}
          >
            {isEventActive ? "✅" : "⛔"} {eventMessage}
          </div>
        )}
        {activeReadOnlyMode && activeReadOnlyMessage && (
          <div
            style={{
              borderRadius: "10px",
              padding: "12px 18px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "13.5px",
              fontWeight: 500,
              background: "#fef2f2",
              color: "#991b1b",
              border: "1px solid #fca5a5",
            }}
          >
            ⚠️ {activeReadOnlyMessage}
          </div>
        )}

        {/* Add new form */}
        {showAddNew && <Form isUpdate={false} {...formProps} />}

        {/* List */}
        {savedInternships.length > 0
          ? savedInternships.map((s, i) => (
              <Card key={s.id} saved={s} idx={i} {...cardProps} />
            ))
          : !showAddNew && (
              <div
                style={{
                  textAlign: "center",
                  padding: "52px 24px",
                  background: "#fff",
                  borderRadius: "16px",
                  border: `2px dashed ${COLOR.border}`,
                  color: "#aaa",
                }}
              >
                <div style={{ fontSize: "52px", marginBottom: "14px" }}>📋</div>
                <h3
                  style={{
                    margin: "0 0 8px",
                    color: "#888",
                    fontSize: "16px",
                    fontWeight: 700,
                  }}
                >
                  No Applications Found
                </h3>
                <p style={{ margin: "0 0 22px", fontSize: "14px" }}>
                  You have not submitted any internship application for this
                  semester.
                </p>
                {isEventActive &&
                  !activeReadOnlyMode &&
                  savedInternships.length < MAX_INTERNSHIPS && (
                    <button
                      style={{ ...btn.save, padding: "12px 28px" }}
                      onClick={() => {
                        resetForm();
                        setShowAddNew(true);
                      }}
                    >
                      ＋ Add Your First Internship
                    </button>
                  )}
              </div>
            )}

        {/* Modals */}
        {isFirstSubmit && <ConfirmModal isExt={false} />}
        {isFirstSubmitExt && <ConfirmModal isExt={true} />}
      </div>
    </StudentTemplate>
  );
};

export default StudentInternship;
